import React, {Component} from 'react';
import Api from "../../utils/Api";
import moment from "moment/moment";
import CSVDownloadButton from "../../utils/CSVDownloadButton";
import PDFDownloadButton from "../../utils/PDFDownloadButton";
import DOMDownloadButton from "../../utils/DOMDownloadButton";
import TimerangeSelector from "../../globalComponents/TimerangeSelector";
import MachineMultiSelector from "../../globalComponents/MachineMultiSelector";
import ShiftSelector from "../../globalComponents/ShiftSelector";
import TimeRange from "../../utils/TimeRange";
import ZSKPieChart from "../../utils/charts/ZSKPieChart";
import MachineStates from "../../utils/MachineStates";
import LoadingSplash from "../../globalComponents/LoadingSplash";
import CheckBoxField from "../../utils/CheckBoxField";
import GroupingStatusSelector from "../../globalComponents/GroupingStatusSelector";
import StaticFunctions from "../../utils/StaticFunctions";
import domtoimage from "dom-to-image";

class ReportMachinePerformance extends Component {
    /*
        static propTypes = {
            baseData: PropTypes.object.isRequired,
            machines: PropTypes.instanceOf(Machines).isRequired,
        };
    */
    constructor(props) {

        super(props);

        this.api = new Api();

        this.translate = this.props.baseData.translate;
        this.userLanguage = this.props.baseData.userData.language;
        this.timeFormats = this.props.baseData.languages.getFormatByLanguage(this.userLanguage);
        this.saveSettings = this.props.baseData.saveSettings;
        this.resizeTimer = null;
        this.groupingOpt = [
            {id: 'singleStates', name: this.translate('singleStatus')},
            {id: 'groupedStates', name: this.translate('groupedStatus')}
        ];
        this.csvContent = {
            csvTitle: [],
            columns: [],
            data: [],
        };

        this.state = {
            reportStatus: null,
            reportParams: null,
            timeRange: null, // TimeRange
            showMachineSwitchedOffStatus: false,
            selectedMachines: null,
            selectedGrouping: 'groupedStates',
            currentCompanyId: props.baseData.companies.currentCompany ? props.baseData.companies.currentCompany.id : null,
            requestedParams: null, // stringified parameters to prevent double-request
            chartAreaWidth: 800,
            chartData: null,
            errorData: null,
            pdfChartUrls: null
        };

        this.reportKey = 'machinePerformance';

        this.chartAreaRef = React.createRef();

        this.updateDimensions = this.updateDimensions.bind(this);
        this.requestReport = this.requestReport.bind(this);
        this.groupResultByStatus = this.groupResultByStatus.bind(this);
        this.requestPdfContent = this.requestPdfContent.bind(this);
        this.timerangeChanged = this.timerangeChanged.bind(this);
        this.machineSelected = this.machineSelected.bind(this);
        this.toggleCheckBox = this.toggleCheckBox.bind(this);

    }

    componentWillMount() {

        const newState = {};

        // Timerange aus den userprops laden
        const tr = new TimeRange();
        const momentYesterday = moment().add(-1, 'days');

        tr.setRange(momentYesterday.clone().startOf('day'), momentYesterday.clone().endOf('day'));

        if (tr && tr.isValid()) newState.timeRange = tr;

        // Machines aus den userprops laden
        const machines = this.getReportSetting('machines');

        if (machines) {
            const parseMachines = JSON.parse(machines);

            if (parseMachines.length > 0) newState.selectedMachines = parseMachines;
        }

        // Shift aus den userprops laden
        const sid = this.getReportSetting('shiftId');

        if (sid) {
            const shift = this.props.baseData.shifts.getShiftById(sid);

            if (shift) newState.selectedShift = shift;
        }

        // Grouping aus den userprops laden
        const grouping = this.getReportSetting('grouping');

        if (grouping) newState.selectedGrouping = grouping;

        // Show machine switchedoff status aus den userprops laden
        const showMachineSwitchedOffStatus = this.getReportSetting('showMachineSwitchedOffStatus', false);

        if (showMachineSwitchedOffStatus) newState.showMachineSwitchedOffStatus = true;

        if (newState) {
            this.setState(newState);
        }

    }

    componentDidMount() {

        this.updateDimensions();

        window.addEventListener("resize", this.updateDimensions);

    }

    componentWillUnmount() {

        window.removeEventListener("resize", this.updateDimensions);

    }

    componentWillReceiveProps(nextProps) {

        // on company-change - clear chartdata and selected machine
        if (nextProps.baseData.companies && nextProps.baseData.companies.currentCompany &&
            nextProps.baseData.companies.currentCompany.id !== this.state.currentCompanyId) {

            this.setState({
                chartData: null,
                selectedMachines: null,
                requestedParams: null,
                pdfChartUrls: null,
                currentCompanyId: nextProps.baseData.companies.currentCompany.id
            });
        }

    }

    getReportSetting(key, defaultValue = null) {

        const settings = this.props.baseData.userData.settings ? this.props.baseData.userData.settings : {};
        const reportSettings = (settings.hasOwnProperty('reports') && settings.reports.hasOwnProperty(this.reportKey)) ? settings.reports[this.reportKey] : {};

        return (reportSettings.hasOwnProperty(key)) ? reportSettings[key] : defaultValue;

    }

    updateDimensions() {

        const adjust = () => {
            try {
                this.resizeTimer = 0;
                // try to adjust chart width
                const newWidth = this.chartAreaRef.current && this.chartAreaRef.current.offsetWidth > 300 ? this.chartAreaRef.current.offsetWidth : 300;

                if (newWidth !== this.state.chartAreaWidth) {
                    this.setState({chartAreaWidth: newWidth});
                }
            } catch (e) {
            }
        };

        if (this.resizeTimer === null) adjust();
        if (this.resizeTimer > 0) window.clearTimeout(this.resizeTimer);

        this.resizeTimer = window.setTimeout(adjust, 75);

    }

    getRequestParams() {

        const tr = this.state.timeRange;
        const dateFrom = tr ? tr.getFromMoment() : null;
        const dateTill = tr ? tr.getTillMoment() : null;
        const machineIds = this.state.selectedMachines ? this.getSelectedMachineIds() : [];

        // Check, if all data is set
        if (!dateFrom || !dateTill || machineIds.length === 0) return;
        if (!dateFrom.isValid() || !dateTill.isValid()) return;

        return {
            machines: machineIds,
            companyId: this.props.baseData.companies.currentCompany.id,
            from: dateFrom.unix(),
            till: dateTill.unix(),
            selectedGrouping: this.state.selectedGrouping,
            showMachineSwitchedOffStatus: this.state.showMachineSwitchedOffStatus,
            shift: this.state.selectedShift ? this.state.selectedShift.getRange() : null,
            timezone: this.props.baseData.companies.currentCompany.timezone
        };

    }

    requestReport() {

        if (this.state.reportStatus === 'requested') return null;

        const params = this.getRequestParams();

        if (params === null) return null;

        const sParams = JSON.stringify(params);

        if (sParams === this.state.requestedParams) return null;

        const {reportParams, ...newReportParams} = {...this.state};

        const callback = function(res) {
            if (res) {
                const chartData = this.groupResultByStatus(res);

                if (res) {
                    this.setState({
                        reportStatus: 'ready',
                        chartData: chartData,
                        errorData: null,
                    }, () => {
                        this.requestPdfContent();
                    });
                }
            }
        }.bind(this);

        const error = function(error) {
            if (error) {
                this.setState({
                    reportStatus: 'error',
                    chartData: null,
                    errorData: error,
                });
            }
        }.bind(this);

        this.setState({
            reportStatus: 'requested',
            reportParams: newReportParams,
            requestedParams: sParams,
            chartData: null,
            errorData: null,
            pdfChartUrls: null,
        }, () => {
            this.api.request('GetMachinePerformance', params, callback, error);
        });

    }

    requestPdfContent() {

        const charts = this.state.chartData;
        const pdfCharts = [];

        if (charts) {
            window.setTimeout(() => {
                charts.forEach(
                    (chartData, index) => {
                        let machineName = this.getMachineNameById(chartData.machineId);

                        if (machineName !== undefined && machineName !== null) {
                            const chartId = machineName.toString().replace(/ /g, '-');
                            let domElement = document.getElementById('chart-' + chartId);

                            if (domElement) {
                                domtoimage.toPng(domElement).then((dataUrl) => {
                                    pdfCharts[index] = dataUrl;

                                    if (pdfCharts.length === charts.length) {
                                        this.setState({
                                            pdfChartUrls: (pdfCharts.length > 0) ? pdfCharts : null
                                        });
                                    }
                                });
                            }
                        }
                    }
                );
            }, 2000);
        }

    }

    timerangeChanged(newRange) {

        if (newRange && (newRange instanceof TimeRange) && newRange.isValid()) {
            this.setState({
                timeRange: newRange,
            });

            // Safe Timerange to Userconfig
            if (this.saveSettings) {
                this.saveSettings('reports', {report: this.reportKey, key: 'timeRange', value: newRange.serialize()});
            }
        }

    }

    machineSelected(machines) {

        this.setState({
            selectedMachines: (machines && machines.length > 0) ? machines : null
        });

        // Safe Machines to Userconfig
        if (this.saveSettings && machines) {
            this.saveSettings('reports', {report: this.reportKey, key: 'machines', value: JSON.stringify(machines)});
        }

    }

    shiftSelected(shift) {

        this.setState({
            selectedShift: shift
        });

        // Safe Shift-id to Userconfig
        if (this.saveSettings && shift) {
            this.saveSettings('reports', {report: this.reportKey, key: 'shiftId', value: shift.id});
        }

    }

    groupingSelected(grouping) {

        this.setState({
            selectedGrouping: grouping.id
        });

        // Safe Grouping-id to Userconfig
        if (this.saveSettings && grouping) {
            this.saveSettings('reports', {report: this.reportKey, key: 'grouping', value: grouping.id});
        }

    }

    toggleCheckBox(fieldName, isChecked) {

        const state = {...this.state};

        state[fieldName] = isChecked;

        this.setState(state);

        // Safe option to Userconfig
        if (this.saveSettings && fieldName) {
            this.saveSettings('reports', {report: this.reportKey, key: fieldName, value: isChecked});
        }

    }

    getSelectedMachineIds() {

        const selectedMachines = this.state.selectedMachines;
        const machineIds = [];

        selectedMachines.forEach(
            (machine) => {
                machineIds.push(machine.value);
            }
        );

        return machineIds;

    }

    getMachineNameById(machineId) {

        if (machineId === undefined || machineId === null) return '';
        if (!this.state.reportParams || !this.state.reportParams.selectedMachines) return String(machineId);

        const machine = this.state.reportParams.selectedMachines.find(
            (machine) => {
                return String(machine.value) === String(machineId);
            }
        );

        return (machine) ? machine.label : String(machineId);

    }

    groupResultByStatus(result) {

        result.forEach(
            (machine) => {
                machine.groupedStates = MachineStates.groupingByStatus(machine.states);
            }
        );

        return result;

    }

    getErrorBlock() {

        let errorBlock = null;

        if (this.state.errorData || this.state.reportStatus === 'error') {
            errorBlock = <span className="warningBox ma-bo-10">{this.translate('errorOccured')}</span>;
        } else if (!this.state.reportParams.timeRange || !this.state.reportParams.timeRange.isValid()) {
            errorBlock = <span className="warningBox ma-bo-10">{this.translate('timerangeIsUnset')}</span>;
        } else if (!this.state.reportParams.timeRange || !this.state.reportParams.timeRange.isNotInFuture()) {
            errorBlock = <span className="warningBox ma-bo-10">{this.translate('timerangeIsInFuture')}</span>;
        } else if (!this.state.reportParams.selectedMachines || !this.state.reportParams.selectedMachines) {
            errorBlock = <span className="warningBox ma-bo-10">{this.translate('machineIsUnset')}</span>;
        } else if (this.state.chartData === null) {
            errorBlock = <LoadingSplash color={'dark'}/>;
        } else if (this.state.chartData && this.state.chartData.length === 0) {
            errorBlock = <span className="warningBox ma-bo-10">{this.translate('noDataFound')}</span>;
        }

        return (errorBlock !== null) ? errorBlock : null;

    }

    getChartHeadline(timeRange, otherParams = {}) {

        const params = {
            machine: this.state.reportParams.selectedMachine,
            machineTitle: this.translate('machine'),
            allMachinesTitle: this.translate('allMachines'),
            design: this.state.reportParams.selectedDesign,
            designTitle: this.translate('design'),
            allDesignsTitle: this.translate('allDesigns'),
            timerange: timeRange,
            formatedDate: this.timeFormats.fullDate,
            timerangeTitle: this.translate('timerange'),
            shift: this.state.reportParams.selectedShift,
            shiftTitle: this.translate('shift'),
            groupingOpt: this.groupingOpt,
            groupingId: this.state.reportParams.selectedGrouping,
            groupingTitle: this.translate('grouping')
        };

        return StaticFunctions.createReportHeadlineFromParams({...params, ...otherParams});

    }

    getCsvContent() {

        const selectedMachines = this.state.reportParams.selectedMachines;
        const timeRange = this.state.reportParams.timeRange;
        const chartData = this.state.chartData;
        const tableColumns = this.getTableColumns();
        const csvContent = {
            csvTitle: [],
            csvColumns: [],
            csvData: [],
        };

        if (selectedMachines && chartData) {
            for (let i = 0; i < chartData.length; i++) {
                let chartDataTemp = chartData[i];
                let machineName = this.getMachineNameById(chartDataTemp.machineId);
                let headline = this.getChartHeadline(timeRange, {machine: {label: machineName}});
                let data = (this.state.reportParams.selectedGrouping === 'groupedStates') ? chartDataTemp.groupedStates : chartDataTemp.states;
                let tableRows = (data) ? this.getTableRows(data) : [];
                let totalSeconds = 0;

                //  Dezimalpunkt durch Komma ersetzen, weil Excel daraus ggf. ein Datum machen könnte...
                for (let i in tableRows) {
                    totalSeconds += tableRows[i]['pastTime'];

                    tableRows[i]['pastTime'] = StaticFunctions.convertSecondsToFormatedString(tableRows[i]['pastTime']);
                    tableRows[i]['percentShare'] = tableRows[i]['percentShare'].replace('.', ',');
                }

                //  Leerzeile und Gesamtwert hinzufügen.
                tableRows.push({statusName: ' ', pastTime: ' ', percentShare: ' '});
                tableRows.push({statusName: this.translate('total'), pastTime: StaticFunctions.convertSecondsToFormatedString(totalSeconds), percentShare: 100});

                csvContent.csvTitle.push(headline);
                csvContent.csvColumns.push(tableColumns);
                csvContent.csvData.push(tableRows);
            }
        }

        return csvContent;

    }

    getPdfContent() {

        return {
            charts: (this.state.pdfChartUrls) ? this.state.pdfChartUrls : null,
            orientation: 'L',
            tableColumns: null,
            tableRows: null
        };

    }

    getTableColumns() {

        return [
            {
                key: 'statusName',
                nameKey: 'status',
                sortType: 'string',
            },
            {
                key: 'pastTime',
                nameKey: 'time',
                classes: 'text-right',
                sortType: 'int',
            },
            {
                key: 'percentShare',
                nameKey: 'percentShare',
                classes: 'text-right',
                sortType: 'string',
            },
        ];

    }

    getChartHeader(timeRange, machineName) {

        const headline = this.getChartHeadline(timeRange, {machine: {label: machineName}});

        return (
            <nav>
                <h1>{headline}</h1>
            </nav>
        );

    }

    getTableRows(chartData) {

        const states = MachineStates.getStates();
        const rows = [];

        let totalSeconds = 0;

        for (let ix in chartData) {
            totalSeconds += chartData[ix]
        }

        if (totalSeconds <= 0) return null;

        for (let ix in states) {
            let state = states[ix];
            let seconds = chartData[state.code];

            if (!seconds) continue;

            let pcnt = seconds / totalSeconds * 10000;

            pcnt = Math.round(pcnt);
            pcnt /= 100;

            let statusName = this.translate(ix);
            let percentShare = pcnt;

            rows.push({
                statusName: statusName,
                pastTime: seconds,
                percentShare: StaticFunctions.formatedNumber(percentShare, 2),
                color: state.color
            });
        }

        return StaticFunctions.getSortedData(rows, 'statusName', 'string');

    }

    generateLegend(chartData) {

        const tableRows = this.getTableRows(chartData);
        const rows = [];

        let totalSeconds = 0;

        for (let ix in chartData) {
            totalSeconds += chartData[ix]
        }

        if (tableRows === null || totalSeconds <= 0) return null;

        for (let ix in tableRows) {
            let rowTemp = tableRows[ix];

            let row = (
                <tr key={'legend' + ix}>
                    <td>
                        <span style={{color: rowTemp.color}}>■</span>
                    </td>
                    <td>{rowTemp.statusName}</td>
                    <td style={{textAlign: 'right'}}>{StaticFunctions.convertSecondsToFormatedString(rowTemp.pastTime)}</td>
                    <td style={{color: rowTemp.color, textAlign: 'right'}}>{rowTemp.percentShare} %</td>
                </tr>
            );

            rows.push(row);
        }

        if (rows.length === 0) return null;

        rows.push(<tr key={'legendBlank'}><td colSpan="4">&nbsp;</td></tr>);
        rows.push(
            <tr key={'legendSum'}>
                <td>&nbsp;</td>
                <td>{this.translate('total')}</td>
                <td style={{textAlign: 'right'}}>{StaticFunctions.convertSecondsToFormatedString(totalSeconds)}</td>
                <td style={{textAlign: 'right'}}>100 %</td>
            </tr>
        );

        return (
            <div className="tableLegend">
                <table>
                    <tbody>{rows}</tbody>
                </table>
            </div>
        );

    }

    generateChart(chartData, machineName) {

        if (!chartData && !this.state.requestedParams) return null;
        if (!chartData) return this.renderNoFound();

        const data = [];

        for (let ix in chartData) {
            let state = chartData[ix];
            let ms = MachineStates.get(ix);

            data.push({
                stateId: ix,
                value: state ? state : 0,
                color: ms ? ms.color : '#fff',
                name: ms ? this.translate('status_' + ix) : ix
            })
        }

        return (
            <div className="chartBox">
                <div className="pieChart">
                    <ZSKPieChart
                        data={data}
                        translate={this.translate}
                        height={Math.round(this.state.chartAreaWidth * 0.5)}
                        width={Math.round(this.state.chartAreaWidth * 0.5)}
                    />
                </div>
            </div>
        );

    }

    renderNoFound() {

        return (
            <div className="ma-bo-10">
                <span className="warningBox">{this.translate('noDataFound')}</span>
            </div>
        );

    }

    renderReportArea() {

        if (this.state.reportStatus === null) return null;
        if (this.state.reportStatus === 'requested') return <LoadingSplash color={'dark'}/>;

        const errorBlock = this.getErrorBlock();

        if (errorBlock !== null) return errorBlock;

        return this.state.chartData.map(
            (chartData) => {
                let machineName = this.getMachineNameById(chartData.machineId);
                let data = (this.state.reportParams.selectedGrouping === 'groupedStates') ? chartData.groupedStates : chartData.states;
                let legend = this.generateLegend(data);

                if (legend) {
                    const chartHeader = this.getChartHeader(this.state.reportParams.timeRange, machineName);
                    const chartArea = this.generateChart(data, machineName);
                    const chartId = machineName ? machineName.toString().replace(/ /g, '-') : 'unknown';

                    return (
                        <div id={'chart-' + chartId} key={'chart-' + chartId} className="clearfix">
                            {chartHeader}

                            <div>
                                <div className="col-sm-8" ref={this.chartAreaRef}>
                                    {chartArea}
                                </div>

                                <div className="col-sm-4">
                                    {legend}
                                </div>
                            </div>
                        </div>
                    );
                } else {
                    let chartHeader = this.getChartHeader(this.state.reportParams.timeRange, machineName);
                    const chartId = machineName ? machineName.toString().replace(/ /g, '-') : 'unknown';

                    return (
                        <div id={'chart-' + chartId} key={'chart-' + chartId} className="clearfix">
                            {chartHeader}

                            {this.renderNoFound()}
                        </div>
                    );
                }
            }
        );

    }

    renderRequestButton() {

        const reportParams = this.getRequestParams();
        const sParams = JSON.stringify(reportParams);

        if (this.state.reportStatus === 'requested' || sParams === this.state.requestedParams) {
            return (
                <a className="prewLink disabled-link">{this.translate('requestReport')}</a>
            );
        } else {
            return (
                <a className="prewLink orangeButton" onClick={() => this.requestReport()}>{this.translate('requestReport')}</a>
            );
        }

    }

    renderDownloadButtons() {

        if (this.state.reportStatus !== 'ready' || !this.state.reportParams || this.getErrorBlock() !== null) {
            return (
                <div className="bottomLinks clearfix">
                    <div><button className="disabled-link">{this.translate('csvDownloadLabel') + ' (CSV)'}</button></div>
                    <div><button className="disabled-link">{this.translate('imageDownloadLabel') + ' (PNG)'}</button></div>
                    <div><button className="disabled-link">{this.translate('pdfDownloadLabel') + ' (PDF)'}</button></div>
                </div>
            );
        } else {
            const csvContent = this.getCsvContent();
            const pdfContent = this.getPdfContent();

            return (
                <div className="bottomLinks clearfix">
                    <CSVDownloadButton
                        baseData={this.props.baseData}
                        fileName={this.props.reportName}
                        csvTitle={csvContent.csvTitle}
                        columns={csvContent.csvColumns}
                        data={csvContent.csvData}
                    />

                    <DOMDownloadButton
                        baseData={this.props.baseData}
                        domId={'chartArea'}
                        fileName={this.props.reportName}
                    />

                    <PDFDownloadButton
                        baseData={this.props.baseData}
                        fileName={this.props.reportName}
                        charts={pdfContent.charts}
                        tableColumns={pdfContent.tableColumns}
                        tableRows={pdfContent.tableRows}
                    />
                </div>
            );
        }

    }

    render() {

        const timerangeFormat = this.timeFormats.fullDate;
        const reportArea = this.renderReportArea();
        const tableArea = null;
        const requestButton = this.renderRequestButton();
        const downloadArea = this.renderDownloadButtons();

        return (
            <div>
                <div id="reportSettings" className="clearfix">
                    <div className="clearfix">
                        <div className="col-sm-4">
                            <div className="ma-bo-10">
                                <h4>{this.translate('timerange')}</h4>

                                <div>
                                    <TimerangeSelector
                                        TimeRange={this.state.timeRange}
                                        displayFormat={timerangeFormat}
                                        translate={this.translate}
                                        onTimeRangeChanged={this.timerangeChanged}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-sm-2">
                            <div className="ma-bo-10">
                                <h4>{this.translate('machines')}</h4>

                                <div>
                                    <MachineMultiSelector
                                        machines={this.props.machines}
                                        currentMachines={this.state.selectedMachines}
                                        translate={this.translate}
                                        onMachineSelected={(machine) => this.machineSelected(machine)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-sm-2">
                            <div className="ma-bo-10">
                                <h4>{this.translate('shift')}</h4>

                                <div>
                                    <ShiftSelector
                                        baseData={this.props.baseData}
                                        currentShift={this.state.selectedShift}
                                        translate={this.translate}
                                        onShiftSelected={(shift) => this.shiftSelected(shift)}
                                    />
                                </div>
                            </div>

                            <div className="ma-bo-10">
                                <h4>{this.translate('grouping')}</h4>

                                <div>
                                    <GroupingStatusSelector
                                        translate={this.translate}
                                        currentGrouping={this.state.selectedGrouping}
                                        onGroupingSelected={(grouping) => this.groupingSelected(grouping)}
                                    />
                                </div>
                            </div>

                            <div className="formSettings ma-bo-10">
                                <h4>{this.translate('options')}</h4>

                                <div className="formBox">
                                    <CheckBoxField
                                        name="showMachineSwitchedOffStatus"
                                        isChecked={this.state.showMachineSwitchedOffStatus}
                                        label={this.translate('showMachineSwitchedOffStatus')}
                                        onChange={this.toggleCheckBox}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-sm-2">
                            <div className="ma-bo-10">
                                <h4>{this.translate('downloadReport')}</h4>

                                {downloadArea}
                            </div>
                        </div>
                    </div>

                    <div className="bottomLinks clearfix">
                        {requestButton}
                    </div>
                </div>

                <div id="reportArea" className="clearfix">
                    <div id="chartArea" className="ma-bo-10">
                        {reportArea}
                    </div>

                    <div id="tableArea" className="ma-bo-10">
                        {tableArea}
                    </div>
                </div>
            </div>
        );

    }

}

export default ReportMachinePerformance;
